import {
  FormClickOnly,
  FormField,
  FormSection,
  FormSelect,
  SelectionPickerDialog,
} from '@/components';
import { useCompanyLevels } from '@/features/companies';
import type { Level } from '@/features/company-admin/models';
import { useLanguages } from '@/features/translations';
import { useCurrentCompany } from '@/hooks/useCurrentCompany';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderPickerDialog } from '../FolderPickerDialog';
import { DocumentFileObjectsSectionActions } from './DocumentFileObjectsSection';
import { FileObjectRow } from './types';
import { getCategoryOptions } from '../../utils/getCategoryLabel';

export interface DocumentMetadataState {
  name: string;
  selectedFolder: string | null;
  category: string;
  selectedCompanyLevel?: string;
  companyId?: string | null;
}

interface DocumentMetadataSectionProps {
  state: DocumentMetadataState;
  isLocked: boolean;
  showErrors: boolean;
  initialFolder: { name: string; id: string | null } | null;
  hideFolderSelection?: boolean;
  langauageSetting: LanguageSection;
  onStateChange: (state: DocumentMetadataState, hasError: boolean) => void;
}

interface LanguageSection {
  firstRow: FileObjectRow | undefined;
  isLocked: boolean;
  sectionActions: DocumentFileObjectsSectionActions;
}



/**
 * @component DocumentMetadataSection
 * @description Renders the base metadata controls for a document file, including naming and classification fields.
 *
 * @props
 *   state (DocumentMetadataState, required) - Current state with name, selectedFolder, category, selectedCompanyLevel, companyId.
 *   isLocked (boolean, required) - Disables editing while metadata is read-only.
 *   showErrors (boolean, required) - Whether to show validation errors for missing required values.
 *   onStateChange ((state, hasError) => void, required) - Callback when state changes, includes error flag.
 *
 * @changelog
 *   2026-03-24 - Initial creation for shared document file create/edit flows.
 *   2026-03-24 - Refactored to internalize folder picker and company level picker dialogs.
 */

export function DocumentMetadataSection({
  state,
  isLocked,
  showErrors,
  initialFolder,
  hideFolderSelection,
  langauageSetting,
  onStateChange,
}: DocumentMetadataSectionProps) {
  const { t } = useTranslation();
  const { name, category, selectedCompanyLevel } = state;
  const { companyId } = useCurrentCompany();

  const [activeDialog, setActiveDialog] = useState<'folder' | 'companyLevel' | null>(null);
  const isFolderDialogOpen = activeDialog === 'folder';
  const isCompanyLevelDialogOpen = activeDialog === 'companyLevel';
  const { data: categoriesData = [], isLoading: isLoadingLevels } = useCompanyLevels();

  const [selectedFolder, setSelectedFolder] = useState(initialFolder)

  const hasNameError = !name.trim();
  const nameError = showErrors && hasNameError ? t('documents.createFile.validation.nameRequired') : undefined;

  const folderLabel = (selectedFolder?.id === null || selectedFolder === null || selectedFolder === undefined) ? t('documents.folderPicker.rootLabel') : selectedFolder.name;

  const handleNameChange = (value: string) => {
    onStateChange({ ...state, name: value }, !value.trim());
  };

  const handleFolderSelect = (folder: { name: string; id: string | null }) => {
    setSelectedFolder(folder)
    onStateChange({ ...state, selectedFolder: folder.id }, hasNameError);
    setActiveDialog(null);
  };

  const handleCompanyLevelSelect = (level: Level) => {
    onStateChange({ ...state, selectedCompanyLevel: level.id }, hasNameError);
    setActiveDialog(null);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onStateChange({ ...state, category: e.target.value }, hasNameError);
  };

  const closeDialog = () => setActiveDialog(null);

  const currentLevel = categoriesData.find((level) => level.id === selectedCompanyLevel);


  let folderSelection
  if (hideFolderSelection !== true) {
    folderSelection = <FormClickOnly
      label={t('documents.createFile.fields.folder')}
      value={folderLabel}
      actionLabel={t('documents.createFile.changeFolder')}
      onClick={() => setActiveDialog('folder')}
      disabled={isLocked}
    />
  }
  else {
    folderSelection = <></>
  }

  return (
    <>
      <FormSection
        icon="documents"
        title={t('documents.createFile.sections.nameFolderCategory')}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label={t('documents.createFile.fields.name')}
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            maxLength={255}
            disabled={isLocked}
            mandatory
            error={nameError}
          />

          <FormClickOnly
            loading={isLoadingLevels}
            label={t('documents.createFile.fields.companyLevel')}
            value={currentLevel?.name}
            actionLabel={t('companyAdmin.form.changeLevel')}
            onClick={() => setActiveDialog('companyLevel')}
            disabled={isLocked || !companyId}
            mandatory
          />


          <FormSelect
            label={t('documents.createFile.fields.category')}
            value={category}
            onChange={handleCategoryChange}
            disabled={isLocked}
          >
            {getCategoryOptions(t).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </FormSelect>

          <LanguageSection
            firstRow={langauageSetting.firstRow}
            isLocked={isLocked}
            sectionActions={langauageSetting.sectionActions}
          />


          {folderSelection}
        </div>
      </FormSection>

      <FolderPickerDialog
        open={isFolderDialogOpen}
        initialFolderId={selectedFolder?.id}
        isSubmitting={isLocked}
        onSelect={handleFolderSelect}
        onCancel={closeDialog}
      />

      {companyId && (
        <SelectionPickerDialog
          isOpen={isCompanyLevelDialogOpen}
          onClose={closeDialog}
          header={t('companyAdmin.form.changeLevelTitle')}
          subheader={t('companyAdmin.form.changeLevelWarning')}
          isLoading={isLoadingLevels}
          values={categoriesData.map((level) => ({
            id: level.id,
            value: level.name,
            obj: level
          }))}
          isUpdating={false}
          onSelect={(_, level) => {
            if (level) {
              handleCompanyLevelSelect(level);
            }
          }}
          selectedId={selectedCompanyLevel}
        />
      )}
    </>
  );
}

// Renders the language select for the single file object.
function LanguageSection({ firstRow, isLocked, sectionActions }: LanguageSection) {
  const { t } = useTranslation();
  const { data: languages = [], isLoading: isLoadingLanguages } = useLanguages();

  const selectedLanguageId = firstRow?.values.languageId ?? null;

  const handleLanguageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (!firstRow) return;
    const nextLanguageId = event.target.value === '' ? null : Number(event.target.value);
    sectionActions.onLanguageChange(firstRow.rowId, nextLanguageId);
  };

  return (
    <FormSelect
      label={t('documents.createFile.fields.language')}
      value={selectedLanguageId ?? ''}
      onChange={handleLanguageChange}
      disabled={isLocked || isLoadingLanguages}
    >
      <option value="">{t('documents.createFile.languageNotSpecified')}</option>
      {languages.map((lang) => (
        <option key={lang.id} value={lang.id}>{lang.name}</option>
      ))}
    </FormSelect>
  );
}
